import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import BrandWordmark from "../components/common/BrandWordmark";
import {
  ArrowRight,
  CheckCircle,
  Star,
  Users,
  Code,
  Zap,
  Shield,
  Globe,
  Smartphone,
  Database,
  Palette,
  Brain,
  Cloud,
  ShoppingCart,
  Settings,
  MessageSquare,
  Award,
  TrendingUp,
  Menu,
  X,
  Sun,
  Moon,
  LogIn,
  UserPlus,
  LayoutDashboard,
  Plus,
  Edit3,
  Building,
  Building2,
  Facebook,
  Linkedin,
  Instagram,
  Mail,
  Phone,
} from "lucide-react";
import ContactForm from "../components/ContactForm";
import ReviewsSection from "../components/ReviewsSection";
import PlatformShowcase from "../components/landing/PlatformShowcase";
import { trackPageVisit } from "../utils/trackVisit";

const socialLinks = [
  {
    label: "Facebook",
    href: "https://www.facebook.com/share/1JKY77EFvj/",
    Icon: Facebook,
  },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/aetherflow-systems/",
    Icon: Linkedin,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/aetherflowsystems/",
    Icon: Instagram,
  },
];

const footerContact = [
  {
    label: "Email",
    value: "razakissah.devops@gmail.com",
    href: "mailto:razakissah.devops@gmail.com",
    hint: "We reply within 24 hours",
    Icon: Mail,
  },
  {
    label: "Phone",
    value: "+1 (647) 619-2144",
    href: "tel:+16476192144",
    hint: "Mon–Fri, 8am–6pm EST",
    Icon: Phone,
  },
];

interface ContactFormProps {
  onClose: () => void;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (
      hostname.includes("webcontainer.io") ||
      hostname.includes("webcontainer-api.io")
    ) {
      const baseUrl = hostname.replace("--5173--", "--5002--");
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || "http://localhost:5002/api";
};

const API_BASE_URL = getApiBaseUrl();

const LandingPage: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [eligibilityMessage, setEligibilityMessage] = useState("");
  const [eligibilityStatus, setEligibilityStatus] = useState<
    "checking" | "eligible" | "not-eligible" | null
  >(null);

  useEffect(() => {
    trackPageVisit("/");
  }, []);

  const services = [
    {
      icon: Globe,
      title: "Web Development",
      description: "Full-stack web applications with modern frameworks",
      technologies: ["React", "Node.js", "Python", "PHP"],
    },
    {
      icon: Smartphone,
      title: "Mobile Development",
      description: "Native and cross-platform mobile applications",
      technologies: ["React Native", "Flutter", "iOS", "Android"],
    },
    {
      icon: Database,
      title: "Backend Development",
      description: "Scalable APIs and database architecture",
      technologies: ["Node.js", "Python", "Java", "PostgreSQL"],
    },
    {
      icon: Palette,
      title: "UI/UX Design",
      description: "User-centered design and intuitive interfaces",
      technologies: ["Figma", "Adobe XD", "Sketch", "Prototyping"],
    },
    {
      icon: Brain,
      title: "AI & Machine Learning",
      description: "Intelligent solutions and data-driven insights",
      technologies: ["TensorFlow", "PyTorch", "OpenAI", "Computer Vision"],
    },
    {
      icon: Cloud,
      title: "DevOps & Cloud",
      description: "Infrastructure automation and cloud deployment",
      technologies: ["AWS", "Docker", "Kubernetes", "CI/CD"],
    },
    {
      icon: ShoppingCart,
      title: "E-commerce",
      description: "Complete online store solutions",
      technologies: ["Shopify", "WooCommerce", "Magento", "Custom"],
    },
    {
      icon: Settings,
      title: "Custom Software",
      description: "Tailored solutions for unique business needs",
      technologies: ["Enterprise", "Integration", "Automation", "APIs"],
    },
  ];

  const features = [
    {
      icon: Users,
      title: "Professional Developers",
      description: "Access to pre-screened, skilled developers and designers",
    },
    {
      icon: Zap,
      title: "Fast Delivery",
      description: "Quick turnaround times without compromising quality",
    },
    {
      icon: Shield,
      title: "Quality Assurance",
      description: "Rigorous testing and code review processes",
    },
    {
      icon: MessageSquare,
      title: "24/7 Support",
      description: "Round-the-clock assistance and project management",
    },
  ];

  const stats = [
    { number: "50+", label: "Developers", icon: Users },
    { number: "150+", label: "Projects Completed", icon: CheckCircle },
    { number: "98%", label: "Client Satisfaction", icon: Star },
    { number: "24/7", label: "Support Available", icon: MessageSquare },
  ];

  // Check review eligibility when user tries to write a review
  const checkReviewEligibility = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    setEligibilityStatus("checking");

    try {
      const response = await fetch(`${API_BASE_URL}/reviews/eligibility`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      const data = await response.json();

      if (data?.success && data?.data?.eligible) {
        setEligibilityStatus("eligible");
        setShowReviewForm(true);
      } else {
        setEligibilityStatus("not-eligible");
        setEligibilityMessage(
          data?.data?.reason ||
            "You are not eligible to submit reviews at this time."
        );
      }
    } catch (error) {
      setEligibilityStatus("not-eligible");
      setEligibilityMessage(
        "Unable to check eligibility. Please try again later."
      );
    }
  };

  const handleWriteReview = () => {
    checkReviewEligibility();
  };

  const getDashboardRoute = () => {
    if (!user) return "/dashboard";

    switch (user.role) {
      case "admin":
        return "/dashboard/admin";
      case "developer":
        return "/dashboard/developer";
      case "client":
        return "/dashboard/client";
      default:
        return "/dashboard";
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  return (
    <div
      className={`min-h-screen ${
        theme === "dark" ? "bg-gray-900" : "bg-white"
      }`}
    >
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 ${
          theme === "dark" ? "bg-gray-900/95" : "bg-white/95"
        } backdrop-blur-sm border-b ${
          theme === "dark" ? "border-gray-800" : "border-gray-200"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="group flex items-center gap-2.5 transition-transform duration-300 hover:scale-[1.02]">
              {theme === "dark" ? (
                <img
                  src="/logo1.png"
                  alt="AetherFlow Logo"
                  className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 transition-transform duration-300 group-hover:rotate-3"
                />
              ) : (
                <img
                  src="/logo0.png"
                  alt="AetherFlow Logo"
                  className="w-12 h-12 sm:w-14 sm:h-14 shrink-0 transition-transform duration-300 group-hover:rotate-3"
                />
              )}
              <BrandWordmark theme={theme} variant="marketing" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => scrollToSection("services")}
                className={`${
                  theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors`}
              >
                Services
              </button>
              <button
                onClick={() => scrollToSection("features")}
                className={`${
                  theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors`}
              >
                Features
              </button>
              <button
                onClick={() => scrollToSection("reviews")}
                className={`${
                  theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors`}
              >
                Reviews
              </button>
              <button
                onClick={() => scrollToSection("contact")}
                className={`${
                  theme === "dark"
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                } transition-colors`}
              >
                Contact
              </button>

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg ${
                  theme === "dark"
                    ? "bg-gray-800 text-yellow-400"
                    : "bg-gray-100 text-gray-600"
                } hover:bg-opacity-80 transition-colors`}
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>

              {/* Authentication Buttons */}
              {user ? (
                <button
                  onClick={() => navigate(getDashboardRoute())}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </button>
              ) : (
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => navigate("/login")}
                    className={`inline-flex items-center px-4 py-2 ${
                      theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } transition-colors`}
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In
                  </button>
                  <button
                    onClick={() => navigate("/register")}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Get Started
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center space-x-2">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg ${
                  theme === "dark"
                    ? "bg-gray-800 text-yellow-400"
                    : "bg-gray-100 text-gray-600"
                } hover:bg-opacity-80 transition-colors`}
              >
                {theme === "dark" ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className={`p-2 rounded-lg ${
                  theme === "dark" ? "text-gray-300" : "text-gray-600"
                } hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div
              className={`md:hidden py-4 border-t ${
                theme === "dark" ? "border-gray-800" : "border-gray-200"
              }`}
            >
              <div className="flex flex-col space-y-4">
                <button
                  onClick={() => scrollToSection("services")}
                  className={`text-left ${
                    theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  Services
                </button>
                <button
                  onClick={() => scrollToSection("features")}
                  className={`text-left ${
                    theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  Features
                </button>
                <button
                  onClick={() => scrollToSection("reviews")}
                  className={`text-left ${
                    theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  Reviews
                </button>
                <button
                  onClick={() => scrollToSection("contact")}
                  className={`text-left ${
                    theme === "dark"
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-600 hover:text-gray-900"
                  } transition-colors`}
                >
                  Contact
                </button>

                {/* Mobile Authentication Buttons */}
                {user ? (
                  <button
                    onClick={() => navigate(getDashboardRoute())}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-fit"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </button>
                ) : (
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => navigate("/login")}
                      className={`inline-flex items-center px-4 py-2 ${
                        theme === "dark"
                          ? "text-gray-300 hover:text-white"
                          : "text-gray-600 hover:text-gray-900"
                      } transition-colors w-fit`}
                    >
                      <LogIn className="w-4 h-4 mr-2" />
                      Sign In
                    </button>
                    <button
                      onClick={() => navigate("/register")}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-fit"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div>
              <h1
                className={`text-4xl md:text-6xl font-bold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                } mb-6`}
              >
                Build Your
                <span className="text-blue-600"> Dream Project</span>
                <br />
                With Expert Developers
              </h1>
              <p
                className={`text-xl ${
                  theme === "dark" ? "text-gray-300" : "text-gray-600"
                } mb-8 leading-relaxed`}
              >
                We design and build custom web, mobile, and AI-powered software—
                helping businesses turn ideas into reliable, scalable digital
                products.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                {user ? (
                  <button
                    onClick={() => navigate(getDashboardRoute())}
                    className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
                  >
                    <LayoutDashboard className="w-5 h-5 mr-2" />
                    Go to Dashboard
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => navigate("/register")}
                      className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
                    >
                      Get Started Free
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </button>
                    <button
                      onClick={() => scrollToSection("services")}
                      className={`inline-flex items-center px-8 py-4 ${
                        theme === "dark"
                          ? "border-gray-600 text-gray-300 hover:bg-gray-800"
                          : "border-gray-300 text-gray-700 hover:bg-gray-50"
                      } border rounded-lg transition-colors text-lg font-medium`}
                    >
                      View Services
                    </button>
                  </>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div
                      className={`text-2xl md:text-3xl font-bold ${
                        theme === "dark" ? "text-white" : "text-gray-900"
                      } mb-1`}
                    >
                      {stat.number}
                    </div>
                    <div
                      className={`text-sm ${
                        theme === "dark" ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Platform showcase */}
            <div className="lg:pl-4">
              <PlatformShowcase />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        className={`py-20 ${theme === "dark" ? "bg-gray-800" : "bg-gray-50"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className={`text-3xl md:text-4xl font-bold ${
                theme === "dark" ? "text-white" : "text-gray-900"
              } mb-4`}
            >
              Our Services
            </h2>
            <p
              className={`text-xl ${
                theme === "dark" ? "text-gray-300" : "text-gray-600"
              } max-w-3xl mx-auto`}
            >
              Comprehensive development services to bring your vision to life
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className={`${
                  theme === "dark"
                    ? "bg-gray-900 border-gray-700"
                    : "bg-white border-gray-200"
                } p-6 rounded-xl border hover:shadow-lg transition-shadow`}
              >
                <service.icon className="w-12 h-12 text-blue-600 mb-4" />
                <h3
                  className={`text-xl font-semibold ${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  } mb-3`}
                >
                  {service.title}
                </h3>
                <p
                  className={`${
                    theme === "dark" ? "text-gray-300" : "text-gray-600"
                  } mb-4`}
                >
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {service.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className={`px-2 py-1 text-xs ${
                        theme === "dark"
                          ? "bg-gray-800 text-gray-300"
                          : "bg-gray-100 text-gray-700"
                      } rounded-full`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section
        id="features"
        className={`py-20 ${theme === "dark" ? "bg-gray-900" : "bg-white"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className={`text-3xl md:text-4xl font-bold ${
                theme === "dark" ? "text-white" : "text-gray-900"
              } mb-4`}
            >
              Why Choose AetherFlow?
            </h2>
            <p
              className={`text-xl ${
                theme === "dark" ? "text-gray-300" : "text-gray-600"
              } max-w-3xl mx-auto`}
            >
              We provide everything you need for successful digitizationa and
              automation of your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div
                  className={`w-16 h-16 ${
                    theme === "dark" ? "bg-gray-800" : "bg-blue-50"
                  } rounded-full flex items-center justify-center mx-auto mb-6`}
                >
                  <feature.icon className="w-8 h-8 text-blue-600" />
                </div>
                <h3
                  className={`text-xl font-semibold ${
                    theme === "dark" ? "text-white" : "text-gray-900"
                  } mb-3`}
                >
                  {feature.title}
                </h3>
                <p
                  className={`${
                    theme === "dark" ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews">
        <ReviewsSection />
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className={`py-20 ${theme === "dark" ? "bg-gray-800" : "bg-gray-50"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2
              className={`text-3xl md:text-4xl font-bold ${
                theme === "dark" ? "text-white" : "text-gray-900"
              } mb-4`}
            >
              Get In Touch
            </h2>
            <p
              className={`text-xl ${
                theme === "dark" ? "text-gray-300" : "text-gray-600"
              } max-w-3xl mx-auto mb-8`}
            >
              Ready to start your project? Contact us today for a free
              consultation
            </p>
            <button
              onClick={() => setShowContactForm(true)}
              className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
            >
              <MessageSquare className="w-5 h-5 mr-2" />
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContactForm && (
        <ContactForm onClose={() => setShowContactForm(false)} />
      )}

      {/* Review Form Modal */}
      {showReviewForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div
            className={`${
              theme === "dark" ? "bg-gray-800" : "bg-white"
            } rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto`}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3
                  className={`text-2xl font-bold ${
                    theme === "dark" ? "text-gray-100" : "text-gray-900"
                  }`}
                >
                  Write a Review
                </h3>
                <button
                  onClick={() => setShowReviewForm(false)}
                  className={`p-2 rounded-lg ${
                    theme === "dark" ? "hover:bg-gray-700" : "hover:bg-gray-100"
                  } transition-colors`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p
                className={`${
                  theme === "dark" ? "text-gray-300" : "text-gray-600"
                } mb-6`}
              >
                You'll be redirected to the reviews section where you can submit
                your review.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowReviewForm(false)}
                  className={`px-6 py-3 ${
                    theme === "dark"
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  } border rounded-lg font-medium transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowReviewForm(false);
                    scrollToSection("reviews");
                  }}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Go to Reviews
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer
        className={`${
          theme === "dark"
            ? "bg-gray-900 border-gray-800"
            : "bg-white border-gray-200"
        } border-t py-12`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div
                className={`text-2xl font-bold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                } mb-4`}
              >
                AetherFlow
              </div>
              <p
                className={`${
                  theme === "dark" ? "text-gray-300" : "text-gray-600"
                } mb-5`}
              >
                We design and build custom web, mobile, and AI-powered software
                for businesses ready to launch, scale, and grow.
              </p>
              <div className="flex items-center gap-3">
                {socialLinks.map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Follow AetherFlow on ${label}`}
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-200 ${
                      theme === "dark"
                        ? "bg-gray-800 text-gray-300 hover:bg-blue-600 hover:text-white hover:scale-105"
                        : "bg-gray-100 text-gray-600 hover:bg-blue-600 hover:text-white hover:scale-105"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h3
                className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                } mb-4`}
              >
                Services
              </h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#"
                    className={`${
                      theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } transition-colors`}
                  >
                    Web Development
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`${
                      theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } transition-colors`}
                  >
                    Mobile Apps
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`${
                      theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } transition-colors`}
                  >
                    UI/UX Design
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`${
                      theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } transition-colors`}
                  >
                    AI Solutions
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3
                className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                } mb-4`}
              >
                Company
              </h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#"
                    className={`${
                      theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } transition-colors`}
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`${
                      theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } transition-colors`}
                  >
                    Careers
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className={`${
                      theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } transition-colors`}
                  >
                    Contact
                  </a>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className={`${
                      theme === "dark"
                        ? "text-gray-300 hover:text-white"
                        : "text-gray-600 hover:text-gray-900"
                    } transition-colors`}
                  >
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3
                className={`text-lg font-semibold ${
                  theme === "dark" ? "text-white" : "text-gray-900"
                } mb-4`}
              >
                Get in touch
              </h3>
              <div className="space-y-5">
                {footerContact.map(({ label, value, href, hint, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    className={`group block transition-colors ${
                      theme === "dark"
                        ? "hover:text-white"
                        : "hover:text-gray-900"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon
                        className={`w-4 h-4 shrink-0 ${
                          theme === "dark" ? "text-cyan-400" : "text-blue-600"
                        }`}
                      />
                      <span
                        className={`text-sm font-semibold ${
                          theme === "dark" ? "text-gray-200" : "text-gray-800"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    <p
                      className={`text-sm font-medium pl-6 whitespace-nowrap ${
                        theme === "dark"
                          ? "text-gray-300 group-hover:text-white"
                          : "text-gray-600 group-hover:text-gray-900"
                      }`}
                    >
                      {value}
                    </p>
                    <p
                      className={`text-xs pl-6 mt-0.5 ${
                        theme === "dark" ? "text-gray-500" : "text-gray-500"
                      }`}
                    >
                      {hint}
                    </p>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <div
            className={`mt-8 pt-8 border-t ${
              theme === "dark" ? "border-gray-800" : "border-gray-200"
            } text-center`}
          >
            <p
              className={`${
                theme === "dark" ? "text-gray-400" : "text-gray-600"
              }`}
            >
              © {new Date().getFullYear()} AetherFlow. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
